import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@material-ui/core";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { useTranslation } from "react-i18next";
import PeriodFilterButton from "./PeriodFilterButton";
import PeriodFilterModal from "./PeriodFilterModal";
import {
  buildPeriodSummary,
  resolvePreset,
  MONTH_ABBREVS,
} from "./PeriodFilterModal";

/**
 * Controles do lado direito da barra de filtros:
 *   1. Botão de período (chip cinza + modal especializado)
 *   2. Ícone para voltar aos filtros padrão
 *   3. Checkbox de persistência de filtros entre views
 */
export default function FilterBarExtras({
  availablePeriods,
  selectedPeriodKeys,
  activePreset,
  onPeriodApply,
  onReset,
  persist,
  onPersistChange,
  clientColor,
  hidePeriod,
}) {
  const color = clientColor || "#4caf50";
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  // Abreviações de mês no idioma atual, para buildPeriodSummary
  const periodSummaryOpts = useMemo(
    () => ({
      monthAbbrevs: Object.fromEntries(
        Object.keys(MONTH_ABBREVS).map((m) => [
          m,
          t(`results.filter_bar.period.month_abbrevs.${m}`),
        ]),
      ),
      noPeriodLabel: t("results.filter_bar.period.no_period"),
      nMonthsFn: (n) =>
        n === 1
          ? t("results.filter_bar.period.n_months_singular", { count: n })
          : t("results.filter_bar.period.n_months_plural", { count: n }),
    }),
    [t],
  );

  // Converte availablePeriods para array de chaves "year-month" ordenadas
  const availableKeys = useMemo(
    () =>
      [...availablePeriods]
        .sort((a, b) => a.year * 100 + a.month - (b.year * 100 + b.month))
        .map((p) => `${p.year}-${p.month}`),
    [availablePeriods],
  );

  // Período sempre em formato literal (jan/24 – fev/25), mesmo para presets.
  // Quando modo=group, resolve o preset para chaves concretas antes de formatar.
  const effectiveKeys = useMemo(() => {
    if (selectedPeriodKeys && selectedPeriodKeys.size > 0)
      return selectedPeriodKeys;
    if (activePreset && availableKeys.length > 0)
      return resolvePreset(activePreset, availableKeys);
    return null;
  }, [selectedPeriodKeys, activePreset, availableKeys]);

  const periodActive = effectiveKeys && effectiveKeys.size > 0;
  const summary = periodActive
    ? buildPeriodSummary(effectiveKeys, periodSummaryOpts)
    : t("results.filter_bar.period.no_period");

  return (
    <>
      {/* ── separador ────────────────────────────────────────────────── */}
      <BarDivider />

      {/* ── botão de período ─────────────────────────────────────────── */}
      {!hidePeriod && (
        <PeriodFilterButton
          summary={summary}
          onClick={() => setModalOpen(true)}
          active={periodActive}
          clientColor={color}
        />
      )}

      {/* ── separador ────────────────────────────────────────────────── */}
      {!hidePeriod && <BarDivider />}

      {/* ── reset — forwardRef garante que o Tooltip do MUI funcione ─── */}
      <Tooltip title={t("results.filter_bar.reset_tooltip")} placement="top">
        <ResetButton onClick={onReset} color={color} />
      </Tooltip>

      {/* ── persistência ─────────────────────────────────────────────── */}
      <Tooltip title={t("results.filter_bar.persist_tooltip")} placement="top">
        <div
          onClick={() => onPersistChange && onPersistChange(!persist)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            cursor: "pointer",
            gap: 6,
            userSelect: "none",
          }}
        >
          {/* Toggle slider customizado — mesmo padrão do AlignmentSession */}
          <div
            style={{
              position: "relative",
              width: 26,
              height: 16,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: persist ? color : "#ccc",
                borderRadius: 16,
                transition: "background-color 0.25s",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 2,
                left: persist ? 12 : 2,
                width: 12,
                height: 12,
                backgroundColor: "#fff",
                borderRadius: "50%",
                boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                transition: "left 0.25s",
              }}
            />
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: persist ? "#555" : "#aaa",
            }}
          >
            {t("results.filter_bar.persist_label")}
          </span>
        </div>
      </Tooltip>

      {/* ── modal de período ─────────────────────────────────────────── */}
      {!hidePeriod && (
        <PeriodFilterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onApply={(result) => {
            setModalOpen(false);
            if (onPeriodApply) onPeriodApply(result);
          }}
          availablePeriods={availablePeriods}
          initialSelectedKeys={selectedPeriodKeys}
          initialPreset={activePreset}
          clientColor={color}
        />
      )}
    </>
  );
}

// ── sub-componentes locais ────────────────────────────────────────────────────

// borderRight em vez de background+width para evitar sub-pixel rendering inconsistente
function BarDivider() {
  return (
    <div
      style={{
        width: 0,
        height: 14,
        borderRight: "1px solid #d8d8d8",
        margin: "0 8px",
        flexShrink: 0,
        alignSelf: "center",
      }}
    />
  );
}

BarDivider.propTypes = {};

// forwardRef é necessário para que o Tooltip do MUI v4 consiga
// anexar o ref ao elemento DOM e posicionar o tooltip corretamente
const ResetButton = React.forwardRef(function ResetButton(
  { onClick, color, ...tooltipProps },
  ref,
) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      ref={ref}
      onClick={onClick}
      {...tooltipProps}
      onMouseEnter={(e) => {
        setHovered(true);
        if (tooltipProps.onMouseEnter) tooltipProps.onMouseEnter(e);
      }}
      onMouseLeave={(e) => {
        setHovered(false);
        if (tooltipProps.onMouseLeave) tooltipProps.onMouseLeave(e);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 22,
        height: 22,
        borderRadius: "50%",
        cursor: "pointer",
        color: hovered ? color : "#aaa",
        background: hovered ? "#f0f0f0" : "transparent",
        transition: "all 0.15s",
        flexShrink: 0,
      }}
    >
      <SettingsBackupRestoreIcon style={{ fontSize: 14 }} />
    </div>
  );
});

ResetButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  color: PropTypes.string.isRequired,
};

FilterBarExtras.defaultProps = {
  availablePeriods: [],
  selectedPeriodKeys: null,
  activePreset: "l12m",
  persist: false,
  hidePeriod: false,
  onReset: () => {},
  onPeriodApply: () => {},
  onPersistChange: () => {},
};

FilterBarExtras.propTypes = {
  availablePeriods: PropTypes.arrayOf(
    PropTypes.shape({ year: PropTypes.number, month: PropTypes.number }),
  ),
  selectedPeriodKeys: PropTypes.instanceOf(Set),
  activePreset: PropTypes.string,
  onPeriodApply: PropTypes.func,
  onReset: PropTypes.func,
  persist: PropTypes.bool,
  hidePeriod: PropTypes.bool,
  onPersistChange: PropTypes.func,
  clientColor: PropTypes.string.isRequired,
};
